import React, {
  forwardRef,
  Ref,
  useImperativeHandle,
  useState,
  useEffect,
  useContext,
  useRef,
} from "react";
import _ from 'lodash';
import {
  TextField,
  MenuItem,
  Box,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { StyledTooltip } from "../../components/styled-tooltip/styled-tooltip";
import pubSub, { EVENTS } from "../../config/pubSub";
import { OrganizationsContext } from "./organizations-context";
import { useService } from "./organizations-service";
import { AppConfig } from "../../context/context";
import { APP_LANGUAGES } from "../../utils/constants";
import { isEmailValid } from "../../utils/emailValidate";
import { StyledButton } from "../../components/styled-button/styled-button";

type INameSubscriptionStepModel = {
  name: string;
  displayName: string;
  email: string;
  url?: string;
  locale?: string;

};

export type IAddEditProviderModel = {
  0: INameSubscriptionStepModel;
  isActivated?: boolean | undefined;
  isValid: boolean;
  hasChanges?: boolean;
};

export type IEditProviderModel = {
  0: INameSubscriptionStepModel;
  isActivated?: boolean | undefined;
  isValid: boolean;
};

const FormWrapper = styled(Box)(({ theme }) => ({
  "& label.MuiFormLabel-root": {
    fontSize: "inherit",
  },
  "& strong": {
    margin: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginLeft: 0,
    display: "inline-block",
  },
  "& .MuiTextField-root": {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    "& div.MuiOutlinedInput-root": {
      fontSize: "inherit",
      padding: theme.spacing(1),
      borderRadius: "8px",
    },
  },
  "& div.url-field": {
    margin: theme.spacing(1),
    marginLeft: 0,
    "& strong": {
      marginTop: 0,
      marginRight: theme.spacing(1),
    },
  },
  "& .label": {
    marginBottom: 0,
  },
}));

const ActionsWrapper = styled(Box)(({ theme }) => ({
  textAlign: "right"
}));

interface IValidatorProps {
  model: INameSubscriptionStepModel;
  validate: any;
}

const validateName = (props: IValidatorProps) => {
  const { validate, model } = props;

  if (!validate.name) {
    validate.name = {
      touched: true,
      error: false,
    };
  }

  validate.name.message = "";

  if (!model.name) {
    validate.name.message = "validate_required";
    validate.name.error = true;
    return validate;
  }

  if (model.name.length < 3) {
    validate.name.message = "validate_minlength";
    validate.name.minLength = 3;
    validate.name.error = true;
    return validate;
  } else {
    validate.name.message = "validate_minlength";
    validate.name.minLength = 3;
    validate.name.error = false;
    return validate;
  }
};

const validateDisplayName = (props: IValidatorProps) => {
  const { validate, model } = props;

  if (!validate.displayName) {
    validate.displayName = {
      touched: true,
      error: false,
    };
  }

  validate.displayName.message = "";

  if (!model.displayName.trim()) {
    validate.displayName.message = "validate_required";
    validate.displayName.error = true;
  } else {
    validate.displayName.message = "validate_required";
    validate.displayName.error = false;
  }

  return validate;
};

const validateEmail = (props: IValidatorProps) => {
  const { validate, model } = props;

  if (!validate.email) {
    validate.email = {
      touched: true,
      error: false,
    };
  }

  validate.email.message = "";

  if (!model.email) {
    validate.email.message = "validate_required";
    validate.email.error = true;
    return validate;
  }

  if (!isEmailValid(model.email)) {
    validate.email.message = "validate_invalid";
    validate.email.error = true;
  } else {
    validate.email.message = "validate_invalid";
    validate.email.error = false;
  }
  return validate;
};

export const validateStep = (
  model: INameSubscriptionStepModel,
  validate: any,
  el: string,
  mgmtIdFlag: boolean,
) => {
  if (mgmtIdFlag === true) {
    return {
      ...validateName({ model, validate }),
      ...validateDisplayName({ model, validate }),
      ...validateEmail({ model, validate })
    };
  } else {
    return {
      ...validateName({ model, validate }),
      ...validateDisplayName({ model, validate }),
      ...validateEmail({ model, validate }),
    };
  }
};

export interface IOrgUpsertRefObject {
  resetValidate: () => void;
}

type IProps = {
  model: IAddEditProviderModel;
  handleModelChange: (updatedModel: any) => void;
  containerClass?: string;
  readOnlyName?: boolean;
  readOnlyEmail?: boolean;
  initialEmail?: string;
};

export const OrganizationUpsertForm = forwardRef<IOrgUpsertRefObject, IProps>((props, ref) => {
  const { t } = useTranslation();
  const appContext = useContext(AppConfig);
  const [validate, setValidate] = useState<any>({});
  const { orgService } = useService();
  const { selectedOrg } = useContext(OrganizationsContext);
  const [openTooltips, setOpenTooltips] = useState<any>({});
  const [currentEmail, setCurrentEmail] = useState<string>(props.initialEmail || "");
  const [isLoading, setIsLoading] = useState(true);
  const initialDataRef = useRef<INameSubscriptionStepModel | null>(null);

  if (!appContext) {
    throw new Error("useService must be used within an AppConfigProvider");
  }

  const { config } = appContext;

  useImperativeHandle(
    ref,
    () => ({
      resetValidate: () => {
        setValidate({});
      },
    }),
    []
  );

  useEffect(() => {
    // Set initial values only once when component mounts and has required data from props
    if (props.model && props.model[0] && props.model[0].email && props.initialEmail) {
      setCurrentEmail(props.initialEmail); // Use initialEmail from props for initial render
      setIsLoading(false); // Data loaded, set loading to false
      initialDataRef.current = _.cloneDeep(props.model[0]); // Store initial data
      const updatedModel = JSON.parse(JSON.stringify(props.model[0]));
      validateField(updatedModel, ""); // Call validateField on initialization
    }
  }, [props.initialEmail]);


  useEffect(() => {
    pubSub.subscribe(EVENTS.PP_ERROR_ORGANIZATION_DUP, (error: any) => {
      if (!validate.name) {
        validate.name = {
          touched: true,
          error: false,
        };
      }
      validate.name.message = "";
      const newValidate: any = { ...validate };
      newValidate["name"]["error"] = true;
      newValidate["name"]["message"] = t("cmp_organization_name_dup_error");
      setValidate(newValidate);
      setOpenTooltips({ name: newValidate.name.error });
    });
  }, [t, validate]);

  useEffect(() => {
    if (!selectedOrg) {
      const updatedModel = JSON.parse(JSON.stringify(props.model[0]));    
      updateParentModel(updatedModel, "name");
    }
  }, [selectedOrg]);

  const handleResendMail = async () => {
    try {
      if (selectedOrg !== null) {
        await orgService.resendEmail(selectedOrg);
        pubSub.publish(EVENTS.SUCCESS, t("message_resend_email"));
      }
    } catch (error) {
      pubSub.publish(EVENTS.ERROR, "common_error_msg");
    }
  };

  /** override for Tooltip.onClose */
  const handleCloseTooltip = (event: any) => {
    const elName = event.target.name;
    setOpenTooltips({ [elName]: false });
  };

  /** override for Tooltip.onOpen */
  const handleOpenTooltip = (event: any) => {
    const elName = event.target.name;
    setOpenTooltips({ [elName]: true });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const el: HTMLInputElement = event.target as HTMLInputElement;

    if (!el.name) {
      console.error("The input element has no name attribute.");
      return;
    }

    const updatedModel = JSON.parse(JSON.stringify(props.model[0]));
    updatedModel[el.name] = el.value;

    const name = el.name.trim().toLowerCase();

    if (name === "name") {
      const nameRegex = /[^0-9a-z]/gi;
      updatedModel[el.name] = el.value.replace(nameRegex, "");
    }

    if (name === "referenceid") {
      const refRegEx = /[^0-9a-z\/]/gi;
      updatedModel[el.name] = el.value.replace(refRegEx, "");
    }

    if (name === "displayname") {
      const displayNameRegex = /[\^\\/:,;*?"<>|[\]{}!@#$%^`~&'.()_+=]/g;
      updatedModel[el.name] = el.value.replace(displayNameRegex, "");
    }

    updateParentModel(updatedModel, el.name);
  };

  const sanitizeModel = (inputModel: any) => {
    const sanitized = JSON.parse(JSON.stringify(inputModel));
    Object.keys(sanitized).forEach((key) => {
      if (!(sanitized[key] instanceof Array)) {
        sanitized[key] = sanitized[key]?.trim().replace(/\s\s+/g, " ");
      }
    });
    return sanitized;
  };

  const handleFocusAndBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (event.type === "focus") {
      setOpenTooltips({ [event.target.name]: false }); // hide async errors
    }

    const el: HTMLInputElement = event.target as HTMLInputElement;
    const cleanModel = sanitizeModel(props.model[0]);
    updateParentModel(cleanModel, el.name);
  };

  const handleKeyPress = (event: any) => {
    const elName: string = event.target.name;
    const elValue: string = String.fromCharCode(event.which);
    const updatedModel = JSON.parse(JSON.stringify(props.model[0]));

    if (!elName) {
      console.error("The input element has no name attribute.");
      return;
    }

    switch (elName.trim().toLowerCase()) {
      case "name":
        const nameRegex = /[^\W_]/g;
        if (!nameRegex.test(elValue)) {
          event.preventDefault();
          return;
        }
        break;
      case "displayname":
        const displayNameRegex = /[^\\/:,;*?"<>|[\]{}!@#$%^`~&'.()_+=]/g;
        if (!displayNameRegex.test(elValue)) {
          event.preventDefault();
          return;
        }
        break;
      default:
        break;
    }
    updateParentModel(updatedModel, elName);
  };

  const handlePaste = (event: any) => {
    const el = event.target;
    const elName = el.name;
    const updatedModel = JSON.parse(JSON.stringify(props.model[0]));

    if (!el.name) {
      console.error("The input element has no name attribute.");
      return;
    }

    switch (elName.trim().toLowerCase()) {
      case "name":
        setTimeout(() => {
          const nameRegex = /\W/g;
          updatedModel[elName] = el.value.replace(nameRegex, "");
          updateParentModel(updatedModel, elName);
        }, 20);
        break;
      case "displayname":
        setTimeout(() => {
          const displayNameRegex = /[\^\\/:,;*?"<>|[\]{}!@#$%^`~&'.()_+=]/g;
          updatedModel[elName] = el.value.replace(displayNameRegex, "");
          updateParentModel(updatedModel, elName);
        }, 20);
        break;
      default:
        break;
    }
    updateParentModel(updatedModel, elName);
  };

  const updateParentModel = (updatedModel: INameSubscriptionStepModel, fieldName: string) => {
    const isValid = validateField(updatedModel, fieldName);
    updatedModel.name = updatedModel.name?.toLowerCase();

    // Compare with initial data to determine if changes were made
    const hasChanges = !_.isEqual(initialDataRef.current, updatedModel);

    props.handleModelChange({
      ...props.model,
      0: updatedModel,
      isValid: isValid,
      hasChanges: hasChanges,
    });
  };

  const validateField = (
    updatedModel: INameSubscriptionStepModel,
    fieldName: string,
  ) => {
    const _validate = validateStep(
      updatedModel,
      validate,
      fieldName,
      config.requireManagementId,
    );
    setValidate(_validate);

    let isValid = true;
    const errorField: string | undefined = Object.keys(_validate).find(
      (key: string) => _validate[key].error === true,
    );

    if (errorField) {
      isValid = false;
    }
    return isValid;
  };

  return (
    <Box className={props.containerClass} sx={{ width: "701px" }}>
      <FormWrapper>
        <Typography variant="h5" sx={{ marginBottom: "8px", fontWeight: "700" }}>{t("cmp_organization_information")}</Typography>
        <Box display="flex" flexDirection="column" alignItems="stretch">
          <StyledTooltip
            open={!!openTooltips.name && validate.name}
            onClose={handleCloseTooltip}
            onOpen={handleOpenTooltip}
            invalid={validate.name?.error}
            title={
              (validate.name &&
                t(validate.name.message, {
                  item: t("cmp_organization_name_label"),
                  minLength: validate?.name?.minLength,
                })) ||
              ""
            }
            disableHoverListener
          >
            <TextField
              fullWidth
              disabled={(selectedOrg != null && selectedOrg.length > 0)}
              error={
                validate.name && validate.name.touched && validate.name.error
              }
              id="txtOrganizationName"
              variant="outlined"
              label={t("cmp_organization_name_label")}
              name="name"
              value={props.model[0].name}
              inputProps={{
                maxLength: 36,
                style: { textTransform: "lowercase" },
              }}
              onChange={handleChange}
              onFocus={handleFocusAndBlur}
              onBlur={handleFocusAndBlur}
              onClick={handleKeyPress}
              onPaste={handlePaste}
            />
          </StyledTooltip>
          <StyledTooltip
            open={!!openTooltips.displayName && validate.displayName}
            onClose={handleCloseTooltip}
            onOpen={handleOpenTooltip}
            invalid={validate.displayName && validate.displayName.error}
            title={
              (validate.displayName &&
                t(validate.displayName.message, {
                  item: t("cmp_organization_display_name_label"),
                })) ||
              ""
            }
            disableHoverListener
          >
            <TextField
              error={
                validate.displayName &&
                validate.displayName.touched &&
                validate.displayName.error
              }
              id="txtOrganizationDisplayName"
              name="displayName"
              variant="outlined"
              label={t("cmp_organization_display_name_label")}
              value={props.model[0].displayName}
              inputProps={{ maxLength: 220 }}
              onChange={handleChange}
              onFocus={handleFocusAndBlur}
              onBlur={handleFocusAndBlur}
              onClick={handleKeyPress}
              onPaste={handlePaste}
            />
          </StyledTooltip>
          <StyledTooltip
            open={!!openTooltips.email && validate.email}
            onClose={handleCloseTooltip}
            onOpen={handleOpenTooltip}
            invalid={validate.email && validate.email.error}
            title={
              (validate.email &&
                t(validate.email.message, {
                  item: t("cmp_organization_email_label"),
                })) ||
              ""
            }
            disableHoverListener
          >
            <TextField
              disabled={props.model.isActivated}
              error={
                  validate.email &&
                  validate.email.touched &&
                  validate.email.error
              }
              id="txtOrganizationEmail"
              name="email"
              variant="outlined"
              label={t("cmp_organization_email_label")}
              value={props.model[0].email}
              inputProps={{ maxLength: 132 }}
              onChange={handleChange}
              onFocus={handleFocusAndBlur}
              onBlur={handleFocusAndBlur}
              onClick={handleKeyPress}
              onPaste={handlePaste}
            />
          </StyledTooltip>
          {/*
          <TextField
            id="selectLocaley"
            select
            label={t("settings_email_language")}
            name="locale"
            value={props.model[0].locale || ""}
            onChange={(event) =>
              updateParentModel({ ...props.model[0], locale: event.target.value }, "locale")
            }
            size="small"
          >
            {Object.entries(APP_LANGUAGES).map(([key, value]) => (
              <MenuItem key={key} value={value.code}>
                <Typography variant="body2">{value.full}</Typography>
              </MenuItem>
            ))}
          </TextField>
          */}
        </Box>

        {selectedOrg && !props.model.isActivated && (
          <ActionsWrapper>
            <StyledButton
              variant="contained"
              disabled={
                currentEmail !== props.model[0].email || !props.model[0].email
              }
              color="primary"
              onClick={handleResendMail}
              id="resend-email-button"
              data-testid="resend-email-button"
            >
              {t("common_resend_invitation")}
            </StyledButton>
          </ActionsWrapper>
        )}


      </FormWrapper>
    </Box>
  );
});

export default OrganizationUpsertForm;
